import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Check, AlertTriangle, Info } from 'lucide-react';

type ToastItem = { id: number; message: string; icon?: 'check' | 'warn' | 'info' };
type Ctx = { push: (msg: string, icon?: ToastItem['icon']) => void };

const ToastCtx = createContext<Ctx>({ push: () => {} });
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((message: string, icon: ToastItem['icon'] = 'check') => {
    const id = nextId++;
    setItems((s) => [...s, { id, message, icon }]);
    setTimeout(() => setItems((s) => s.filter((t) => t.id !== id)), 3500);
  }, []);
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'fixed', top: 16, right: 16, zIndex: 1000,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            onClick={() => setItems((s) => s.filter((x) => x.id !== t.id))}
            style={{
              background: 'rgba(28,28,30,0.96)', color: '#fff', borderRadius: 10,
              padding: '10px 14px', fontSize: 13.5, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 10,
              minWidth: 220, maxWidth: 380,
              boxShadow: '0 16px 40px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.08)',
              animation: 'slideUp 280ms cubic-bezier(0.16,1,0.3,1)',
              cursor: 'pointer',
            }}
          >
            {t.icon === 'check' && <Check size={16} />}
            {t.icon === 'warn' && <AlertTriangle size={16} />}
            {t.icon === 'info' && <Info size={16} />}
            <span style={{ flex: 1 }}>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() { return useContext(ToastCtx); }
